/**
 * Product Carousel con Embla Carousel
 * Versión experimental para comparar con Slick Carousel
 */

class ProductCarouselEmbla extends HTMLElement {
  constructor() {
    super();
    this.mainEmbla = null;
    this.thumbnailsEmbla = null;
    this.lightboxEmbla = null;
    this.zoomInitialized = false;
    this.initialized = false;
  }

  connectedCallback() {
    // Usar requestAnimationFrame para asegurar que el DOM esté completamente renderizado
    requestAnimationFrame(() => {
      // Obtener referencias del DOM cuando el elemento está conectado
      this.mainProduct = document.querySelector('.main-product');
      this.mainViewport = this.querySelector('.product-main-carousel-embla');
      this.thumbnailsViewport = this.querySelector(
        '.product-thumbnails-carousel-embla'
      );
      this.slidesCount = Number(
        this.thumbnailsViewport?.dataset.slidesCount || 0
      );
      this.variantsStyle =
        this.mainProduct?.querySelector('.main-product-form')?.dataset
          .variantsStyle;

      // Verificar que los elementos existan antes de inicializar
      if (!this.mainViewport) {
        return;
      }

      this.init();
    });
  }

  disconnectedCallback() {
    // Limpiar cuando el elemento es removido del DOM
    this.cleanup();
  }

  cleanup() {
    // Destruir instancias de Embla
    if (this.mainEmbla) {
      this.mainEmbla.destroy();
      this.mainEmbla = null;
    }

    if (this.thumbnailsEmbla) {
      this.thumbnailsEmbla.destroy();
      this.thumbnailsEmbla = null;
    }

    if (this.lightboxEmbla) {
      this.lightboxEmbla.destroy();
      this.lightboxEmbla = null;
    }

    // Limpiar zooms
    if (this.zoomInitialized) {
      this.destroyZooms();
    }

    // Limpiar event listener de variantes si existe
    if (this.variantChangeHandler && this.variantsWrapper) {
      this.variantsWrapper.removeEventListener(
        'variant::changed',
        this.variantChangeHandler
      );
      this.variantChangeHandler = null;
      this.variantsWrapper = null;
    }

    this.initialized = false;
  }

  async init() {
    // Esperar a que Embla esté disponible
    if (typeof EmblaCarousel === "undefined") {
      await this.waitForEmbla();
    }

    // Verificar nuevamente después de esperar
    if (typeof EmblaCarousel === "undefined") {
      console.error("Embla Carousel no está disponible");
      return;
    }

    this.initCarousels();
    this.loadYoutubeVideos();
    this.goToCurrentVariantSlide();
    this.updateOnVariantChange();

    // Inicializar zoom después de que el carrusel esté listo
    setTimeout(() => {
      this.initZoom();
    }, 200);

    this.initLightbox();
  }

  waitForEmbla() {
    return new Promise((resolve) => {
      // Si ya está disponible, resolver inmediatamente
      if (typeof EmblaCarousel !== "undefined") {
        resolve();
        return;
      }

      const checkEmbla = setInterval(() => {
        if (
          typeof EmblaCarousel !== "undefined" ||
          typeof window.EmblaCarousel !== "undefined"
        ) {
          clearInterval(checkEmbla);
          resolve();
        }
      }, 100);

      // Timeout después de 5 segundos
      setTimeout(() => {
        clearInterval(checkEmbla);
        console.warn("Embla Carousel no se cargó después de 5 segundos");
        resolve();
      }, 5000);
    });
  }

  initCarousels() {
    this.initMainCarousel();
    this.initThumbnailsCarousel();
    this.showCarousels();
  }

  initMainCarousel() {
    if (!this.mainViewport) return;

    const Embla =
      typeof EmblaCarousel !== "undefined"
        ? EmblaCarousel
        : window.EmblaCarousel;
    if (!Embla) {
      console.error("EmblaCarousel no está disponible");
      return;
    }

    const options = {
      loop: true,
      align: "start",
      dragFree: false,
      containScroll: "trimSnaps",
    };

    this.mainEmbla = Embla(this.mainViewport, options);

    // Sincronizar con thumbnails
    if (this.thumbnailsEmbla) {
      this.syncThumbnails();
    }

    // Manejar videos cuando cambia el slide
    this.mainEmbla.on("select", () => {
      this.updateActiveSlide();
      this.handleVideoOnSelect();
      // Re-inicializar zoom cuando cambia el slide activo
      setTimeout(() => {
        this.createZooms();
      }, 100);
    });

    // Marcar slide inicial como activo
    this.updateActiveSlide();

    // Botones de navegación
    const prevButton = this.querySelector(".product-carousel-arrow-embla.prev");
    const nextButton = this.querySelector(".product-carousel-arrow-embla.next");

    if (prevButton) {
      prevButton.addEventListener("click", () => this.mainEmbla.scrollPrev());
    }

    if (nextButton) {
      nextButton.addEventListener("click", () => this.mainEmbla.scrollNext());
    }

    // Ocultar flechas si solo hay un slide
    if (this.slidesCount <= 1) {
      const arrowsContainer = this.querySelector(
        ".product-carousel-arrows-embla",
      );
      if (arrowsContainer) arrowsContainer.style.display = "none";
    }
  }

  initThumbnailsCarousel() {
    if (!this.thumbnailsViewport) return;

    const Embla =
      typeof EmblaCarousel !== "undefined"
        ? EmblaCarousel
        : window.EmblaCarousel;
    if (!Embla) {
      console.error("EmblaCarousel no está disponible");
      return;
    }

    const options = {
      loop: false,
      align: "start",
      dragFree: false,
      containScroll: "trimSnaps",
      axis: "y",
      slidesToScroll: 1,
    };

    this.thumbnailsEmbla = Embla(this.thumbnailsViewport, options);

    // Marcar como inicializado para mostrar en móvil
    const wrapper = this.querySelector(".product-thumbnails-wrapper-embla");
    if (wrapper) {
      wrapper.classList.add("embla--initialized");
    }

    // Sincronizar con carrusel principal
    this.syncThumbnails();

    // Click en thumbnails
    const thumbnails = this.querySelectorAll(".embla__slide-thumbnail");
    thumbnails.forEach((thumbnail, index) => {
      thumbnail.addEventListener("click", () => {
        this.mainEmbla.scrollTo(index);
      });
    });
  }

  syncThumbnails() {
    if (!this.mainEmbla || !this.thumbnailsEmbla) return;

    this.mainEmbla.on("select", () => {
      const selectedIndex = this.mainEmbla.selectedScrollSnap();

      // Actualizar thumbnail seleccionado
      const thumbnails = this.querySelectorAll(".embla__slide-thumbnail");
      thumbnails.forEach((thumb, index) => {
        if (index === selectedIndex) {
          thumb.classList.add("embla__slide-thumbnail--selected");
          // Scroll del thumbnail al viewport si es necesario
          this.thumbnailsEmbla.scrollTo(index);
        } else {
          thumb.classList.remove("embla__slide-thumbnail--selected");
        }
      });
    });

    // Cuando se hace click en thumbnail, mover el carrusel principal
    const thumbnails = this.querySelectorAll(".embla__slide-thumbnail");
    thumbnails.forEach((thumbnail, index) => {
      thumbnail.addEventListener("click", () => {
        this.mainEmbla.scrollTo(index);
      });
    });
  }

  updateActiveSlide() {
    if (!this.mainEmbla) return;

    const selectedIndex = this.mainEmbla.selectedScrollSnap();
    const slides = this.mainViewport.querySelectorAll(".embla__slide");

    // Remover clase activa de todos los slides
    slides.forEach((slide) => slide.classList.remove("embla__slide--active"));

    // Agregar clase activa al slide actual
    if (slides[selectedIndex]) {
      slides[selectedIndex].classList.add("embla__slide--active");
    }
  }

  handleVideoOnSelect() {
    const selectedIndex = this.mainEmbla.selectedScrollSnap();
    const slides = this.mainViewport.querySelectorAll(".embla__slide");
    const currentSlide = slides[selectedIndex];

    if (!currentSlide) return;

    // Pausar todos los videos primero
    const allVideos = this.mainViewport.querySelectorAll("video");
    allVideos.forEach((video) => video.pause());

    // Pausar videos de YouTube
    const allIframes = this.mainViewport.querySelectorAll("iframe");
    allIframes.forEach((iframe) => {
      try {
        iframe.contentWindow.postMessage(
          '{"event":"command","func":"pauseVideo","args":""}',
          "*",
        );
      } catch (e) {
        console.log("Error pausando YouTube:", e);
      }
    });

    // Reproducir video del slide actual
    const currentVideo = currentSlide.querySelector("video");
    const currentIframe = currentSlide.querySelector("iframe");

    if (currentVideo) {
      currentVideo.muted = false; // Reproducir con audio
      currentVideo
        .play()
        .catch((e) => console.log("Error reproduciendo video:", e));
    }

    if (currentIframe) {
      try {
        currentIframe.contentWindow.postMessage(
          '{"event":"command","func":"playVideo","args":""}',
          "*",
        );
      } catch (e) {
        console.log("Error reproduciendo YouTube:", e);
      }
    }
  }

  initZoom() {
    // Zoom solo en desktop (>700px)
    if (window.innerWidth <= 700) return;

    this.mobileMediaQuery = window.matchMedia("(max-width: 700px)");
    this.zoomInitialized = false;

    // Crear zooms iniciales
    this.createZooms();

    // Escuchar cambios de tamaño de ventana
    this.mobileMediaQuery.addEventListener("change", (event) => {
      if (event.matches && this.zoomInitialized === true) {
        this.destroyZooms();
      } else if (this.zoomInitialized === false) {
        this.createZooms();
      }
    });
  }

  createZooms() {
    // Verificar que no estemos en mobile
    if (this.mobileMediaQuery && this.mobileMediaQuery.matches) {
      return;
    }

    if (window.innerWidth <= 700) {
      return;
    }

    // Buscar SOLO slides de imagen (excluir videos)
    const imageSlides = this.querySelectorAll(".embla__slide.main-slide-image");

    if (imageSlides.length === 0) {
      return;
    }

    imageSlides.forEach((imageSlide) => {
      // Asegurar que NO sea un slide de video
      if (
        imageSlide.classList.contains("main-slide-video") ||
        imageSlide.classList.contains("main-slide-external-video")
      ) {
        return;
      }

      // Evitar duplicados
      if (imageSlide.dataset.zoomInitialized === "true") {
        return;
      }

      const img = imageSlide.querySelector("img");
      if (!img) return;

      const zoomUrl = imageSlide.dataset.zoomImage || img.getAttribute("src");
      if (!zoomUrl) return;

      // Marcar como inicializado
      imageSlide.dataset.zoomInitialized = "true";

      // Implementar zoom contenido (dentro del contenedor)
      this.setupContainedZoom(imageSlide, img, zoomUrl);
    });

    this.zoomInitialized = true;
  }

  setupContainedZoom(container, img, zoomUrl) {
    // Crear imagen de alta resolución
    const zoomImg = new Image();
    zoomImg.src = zoomUrl;

    zoomImg.onload = () => {
      // Agregar clase para indicar que el zoom está listo
      container.classList.add("zoom-ready");

      // Variables para el zoom
      const zoomLevel = 2; // 2x zoom

      // Manejadores de eventos
      const handleMouseMove = (e) => {
        const rect = container.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        // Calcular porcentaje de posición
        const percentX = x / rect.width;
        const percentY = y / rect.height;

        // Aplicar transform para mover y hacer zoom
        const translateX = (0.5 - percentX) * 100 * (zoomLevel - 1);
        const translateY = (0.5 - percentY) * 100 * (zoomLevel - 1);

        img.style.transform = `scale(${zoomLevel}) translate(${translateX}%, ${translateY}%)`;
      };

      const handleMouseEnter = () => {
        container.classList.add("zooming");
        img.style.transition = "none";
      };

      const handleMouseLeave = () => {
        container.classList.remove("zooming");
        img.style.transform = "scale(1) translate(0, 0)";
        img.style.transition = "transform 0.3s ease";
        img.style.cursor = "default";
      };

      // Agregar event listeners
      container.addEventListener("mouseenter", handleMouseEnter);
      container.addEventListener("mousemove", handleMouseMove);
      container.addEventListener("mouseleave", handleMouseLeave);

      // Guardar referencias para limpieza
      container._zoomHandlers = {
        handleMouseEnter,
        handleMouseMove,
        handleMouseLeave,
      };
    };
  }

  destroyZooms() {
    const imageSlides = this.querySelectorAll(".embla__slide.main-slide-image");
    imageSlides.forEach((imageSlide) => {
      if (imageSlide._zoomHandlers) {
        imageSlide.removeEventListener(
          "mouseenter",
          imageSlide._zoomHandlers.handleMouseEnter,
        );
        imageSlide.removeEventListener(
          "mousemove",
          imageSlide._zoomHandlers.handleMouseMove,
        );
        imageSlide.removeEventListener(
          "mouseleave",
          imageSlide._zoomHandlers.handleMouseLeave,
        );
        delete imageSlide._zoomHandlers;
      }

      const img = imageSlide.querySelector("img");
      if (img) {
        img.style.transform = "";
        img.style.transition = "";
        img.style.cursor = "";
      }

      imageSlide.classList.remove("zoom-ready", "zooming");
      imageSlide.dataset.zoomInitialized = "false";
    });

    this.zoomInitialized = false;
  }

  initLightbox() {
    const lightbox = this.querySelector(".product-lightbox-embla");
    const lightboxViewport = this.querySelector(
      ".product-lightbox-carousel-embla",
    );
    const closeButton = this.querySelector(".product-lightbox-close-embla");
    const mainSlides = this.querySelectorAll(".embla__slide");

    if (!lightbox || !lightboxViewport) return;

    // Inicializar Embla para lightbox
    const Embla =
      typeof EmblaCarousel !== "undefined"
        ? EmblaCarousel
        : window.EmblaCarousel;
    if (Embla) {
      this.lightboxEmbla = Embla(lightboxViewport, {
        loop: true,
        align: "center",
      });
    }

    // Abrir lightbox al hacer click en imágenes
    mainSlides.forEach((slide, index) => {
      const img = slide.querySelector("img");
      if (img) {
        img.addEventListener("click", () => {
          lightbox.classList.add("show");
          this.lightboxEmbla.scrollTo(index);
        });
      }
    });

    // Cerrar lightbox
    if (closeButton) {
      closeButton.addEventListener("click", () => {
        lightbox.classList.remove("show");
      });
    }

    lightbox.addEventListener("click", (e) => {
      if (e.target === lightbox) {
        lightbox.classList.remove("show");
      }
    });

    // Flechas del lightbox
    const lightboxPrev = this.querySelector(
      ".product-lightbox-carousel-arrow-embla.prev",
    );
    const lightboxNext = this.querySelector(
      ".product-lightbox-carousel-arrow-embla.next",
    );

    if (lightboxPrev) {
      lightboxPrev.addEventListener("click", () =>
        this.lightboxEmbla.scrollPrev(),
      );
    }

    if (lightboxNext) {
      lightboxNext.addEventListener("click", () =>
        this.lightboxEmbla.scrollNext(),
      );
    }
  }

  goToCurrentVariantSlide() {
    if (!this.mainEmbla) return;

    const selectedVariant = this.mainProduct?.querySelector("#selectedVariant");
    if (!selectedVariant) return;

    try {
      const variantData = JSON.parse(selectedVariant.textContent);
      if (!variantData || !variantData.featured_media) {
        // Si no hay featured_media, ir al primer slide
        this.mainEmbla.scrollTo(0, true);
        return;
      }

      const variantSlide = this.querySelector(
        `.embla__slide[data-media-id="${variantData.featured_media.id}"]`,
      );

      if (!variantSlide) {
        // Si no se encuentra el slide específico, ir al primer slide
        this.mainEmbla.scrollTo(0, true);
        return;
      }

      const slideIndex = Array.from(
        this.mainViewport.querySelectorAll(".embla__slide"),
      ).indexOf(variantSlide);

      if (slideIndex !== -1) {
        // Usar un pequeño delay para asegurar que Embla esté completamente inicializado
        setTimeout(() => {
          this.mainEmbla.scrollTo(slideIndex, false);
        }, 50);
      }
    } catch (e) {
      console.error("Error obteniendo variante:", e);
      // En caso de error, ir al primer slide
      this.mainEmbla.scrollTo(0, true);
    }
  }

  goToSelectedVariantSlide(variant) {
    if (!variant?.featured_media) return;

    const variantSlide = this.querySelector(
      `.embla__slide[data-media-id="${variant.featured_media.id}"]`,
    );
    if (!variantSlide) return;

    const slideIndex = Array.from(
      this.mainViewport.querySelectorAll(".embla__slide"),
    ).indexOf(variantSlide);
    if (slideIndex !== -1) {
      this.mainEmbla.scrollTo(slideIndex);
    }
  }

  updateOnVariantChange() {
    this.variantsWrapper = this.mainProduct?.querySelector(
      `variant-${this.variantsStyle}`,
    );
    if (!this.variantsWrapper) return;

    // Guardar referencia del handler para poder limpiarlo después
    this.variantChangeHandler = () => {
      const currentVariantInfo = this.variantsWrapper.currentVariant || {
        available: false,
      };
      this.goToSelectedVariantSlide(currentVariantInfo);
    };

    this.variantsWrapper.addEventListener(
      "variant::changed",
      this.variantChangeHandler,
    );
  }

  loadYoutubeVideos() {
    if (typeof YT === "undefined") {
      // Esperar a que YouTube API esté disponible
      const checkYT = setInterval(() => {
        if (typeof YT !== "undefined") {
          clearInterval(checkYT);
          this.initYoutubePlayers();
        }
      }, 100);

      setTimeout(() => clearInterval(checkYT), 5000);
      return;
    }

    this.initYoutubePlayers();
  }

  initYoutubePlayers() {
    if (typeof YT === "undefined") return;

    YT.ready(() => {
      const externalVideos = this.querySelectorAll(".iframe-video-replace-tag");

      externalVideos.forEach((element) => {
        const videoID = element.dataset.id;
        const player = new YT.Player(element, {
          videoId: videoID,
          playerVars: {
            loop: 1,
            origin: window.location.origin,
          },
        });

        element.parentElement.dataset.youtubePlayer = player;
      });
    });
  }

  showCarousels() {
    this.classList.add("show");
  }
}

// Registrar el custom element
if (!customElements.get("product-carousel-embla")) {
  customElements.define("product-carousel-embla", ProductCarouselEmbla);
}
